import { formatThousands } from "./placesAdapter";

// Fixed feature constants.
export const SELECT_ALL_THRESHOLD = 10000; // Select_All_Threshold (Req 1.2)
export const COLLECTION_PAGE_SIZE = 100; // Page_Size — Places API caps page size at 100 (Req 6.4)
export const PAGE_TIMEOUT_MS = 30000; // per-page timeout (Req 9.1)

/**
 * Select_All_Link gating (Req 1.1, 1.2, 1.3, 1.6, 1.7).
 * Returns true iff the link should be offered:
 *   matchTotal is a non-negative integer AND > 0 AND < SELECT_ALL_THRESHOLD
 *   AND not loading AND select-all not already active.
 * Any invalid matchTotal (null/undefined/NaN/negative/non-integer) => false.
 *
 * @param {number} matchTotal Match_Total for the current query.
 * @param {{ loading?: boolean, selectAllMode?: boolean }} [opts]
 * @returns {boolean}
 */
export function shouldOfferSelectAll(matchTotal, { loading, selectAllMode } = {}) {
  return (
    typeof matchTotal === "number" &&
    Number.isInteger(matchTotal) &&
    matchTotal > 0 &&
    matchTotal < SELECT_ALL_THRESHOLD &&
    loading === false &&
    selectAllMode === false
  );
}

/**
 * Select_All_Link text (Req 1.4): "Select all {formatted} places" where
 * formatted uses a comma thousands separator (reuses formatThousands).
 *
 * @param {number} matchTotal Match_Total for the current query.
 * @returns {string}
 */
export function formatSelectAllLabel(matchTotal) {
  return "Select all " + formatThousands(matchTotal) + " places";
}

/**
 * Plan the offset pages for the Azira source (Req 6.4).
 * For a matchTotal and pageSize, returns an ordered array of 1-based page
 * numbers [1, 2, ..., ceil(matchTotal / pageSize)]. matchTotal <= 0 => [].
 * The function stays generic in pageSize, but the engine always drives it with
 * the fixed COLLECTION_PAGE_SIZE of 100 (the Places API max page size).
 *
 * @param {number} matchTotal Match_Total for the current query.
 * @param {number} pageSize Records per page (100 for this feature).
 * @returns {number[]}
 */
export function planOffsetPages(matchTotal, pageSize) {
  if (!(matchTotal > 0)) {
    return [];
  }

  const pageCount = Math.ceil(matchTotal / pageSize);
  const pages = [];
  for (let page = 1; page <= pageCount; page += 1) {
    pages.push(page);
  }
  return pages;
}

/**
 * Progress percentage (Req 7.4). collectedCount / matchTotal as an integer
 * percent clamped to [0, 100]. matchTotal <= 0 => 0. Monotonic in
 * collectedCount for a fixed matchTotal.
 *
 * @param {number} collectedCount Records collected so far.
 * @param {number} matchTotal Match_Total for the current query.
 * @returns {number}
 */
export function computeCollectionProgress(collectedCount, matchTotal) {
  if (!(matchTotal > 0)) {
    return 0;
  }

  const percent = Math.round((collectedCount / matchTotal) * 100);
  return Math.min(100, Math.max(0, percent));
}

/**
 * Whether the Progress_Bar should show for this run (Req 7.1, 7.2):
 * true iff a collection is in progress AND matchTotal > pageSize.
 *
 * @param {boolean} collecting Whether a Match_Collection is running.
 * @param {number} matchTotal Match_Total for the current query.
 * @param {number} pageSize Records per page (100 for this feature).
 * @returns {boolean}
 */
export function shouldShowProgressBar(collecting, matchTotal, pageSize) {
  return collecting === true && matchTotal > pageSize;
}
